/**
 * Activate Account Page (Public - No Authentication Required)
 *
 * Public page for new users to activate their account via invitation token.
 * Users arrive here from invitation email link.
 */
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CheckCircle,
  AlertTriangle,
  Loader2,
  Lock,
  Mail,
  User,
  ArrowRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { invitationsApi } from "@/services/invitationsApi";
import { FechadaNaManutencao } from "@/modules/maintenance/components/FechadaNaManutencao";

interface ValidationResult {
  valid: boolean;
  error?: string;
  invitation?: {
    email: string;
    first_name?: string;
    last_name?: string;
    company_id: string;
    expires_at: string;
  };
}

const ActivateAccountConteudo = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [isActivating, setIsActivating] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setValidationResult({
        valid: false,
        error: "Token de convite não encontrado na URL",
      });
      setIsValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) return;

    try {
      setIsValidating(true);
      const result = await invitationsApi.validate(token);
      setValidationResult(result);

      if (!result.valid) {
        setErrorMessage(result.error || "Token inválido ou expirado");
      }
    } catch (error: any) {
      console.error("Error validating token:", error);
      setValidationResult({
        valid: false,
        error: "Erro ao validar token. Tente novamente.",
      });
      setErrorMessage("Erro ao validar token. Tente novamente.");
    } finally {
      setIsValidating(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Password validation
    if (!password) {
      newErrors.password = "Senha é obrigatória";
    } else if (password.length < 8) {
      newErrors.password = "Senha deve ter pelo menos 8 caracteres";
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = "Senha deve conter pelo menos uma letra maiúscula";
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = "Senha deve conter pelo menos uma letra minúscula";
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = "Senha deve conter pelo menos um número";
    }

    // Password confirmation
    if (!passwordConfirmation) {
      newErrors.passwordConfirmation = "Confirmação de senha é obrigatória";
    } else if (password !== passwordConfirmation) {
      newErrors.passwordConfirmation = "As senhas não coincidem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleActivate = async () => {
    if (!validateForm() || !token) return;

    setIsActivating(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await invitationsApi.activate({
        token,
        password,
        password_confirmation: passwordConfirmation,
      });

      setSuccessMessage("Conta ativada com sucesso! Você será redirecionado para o login...");

      // Store auth tokens if provided
      if (response.access_token) {
        localStorage.setItem(
          "seweb_auth",
          JSON.stringify({
            access_token: response.access_token,
            refresh_token: response.refresh_token,
          })
        );

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (error: any) {
      console.error("Error activating account:", error);
      setErrorMessage(
        error.response?.data?.detail ||
          "Erro ao ativar conta. Verifique os dados e tente novamente."
      );
    } finally {
      setIsActivating(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-lg font-medium">Validando convite...</p>
            <p className="text-sm text-muted-foreground">Aguarde um momento</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!validationResult?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-16 w-16 text-red-500" />
            </div>
            <CardTitle className="text-center text-2xl">Convite Inválido</CardTitle>
            <CardDescription className="text-center">
              {validationResult?.error || "O link de convite não é válido ou expirou"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Entre em contato com o administrador para receber um novo convite.
              </AlertDescription>
            </Alert>
            <Button className="w-full mt-4" variant="outline" onClick={() => navigate("/login")}>
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Activation form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 p-4 rounded-full">
              <User className="h-12 w-12 text-primary" />
            </div>
          </div>
          <CardTitle className="text-center text-2xl">Ativar Conta</CardTitle>
          <CardDescription className="text-center">
            Bem-vindo! Crie sua senha para ativar sua conta.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Success Message */}
          {successMessage && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Invitation Info */}
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{validationResult.invitation?.email}</span>
            </div>
            {validationResult.invitation?.first_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>
                  {validationResult.invitation.first_name} {validationResult.invitation.last_name}
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Expira em:{" "}
              {new Date(validationResult.invitation?.expires_at || "").toLocaleString("pt-BR")}
            </p>
          </div>

          {/* Password Field */}
          <div>
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Senha *
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={errors.password ? "border-red-500 pr-10" : "pr-10"}
                disabled={isActivating}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              Mínimo 8 caracteres, com maiúscula, minúscula e número
            </p>
          </div>

          {/* Password Confirmation Field */}
          <div>
            <Label htmlFor="passwordConfirmation" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Confirmar Senha *
            </Label>
            <div className="relative">
              <Input
                id="passwordConfirmation"
                type={showPasswordConfirmation ? "text" : "password"}
                placeholder="Digite sua senha novamente"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className={errors.passwordConfirmation ? "border-red-500 pr-10" : "pr-10"}
                disabled={isActivating}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswordConfirmation ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.passwordConfirmation && (
              <p className="text-sm text-red-500 mt-1">{errors.passwordConfirmation}</p>
            )}
          </div>

          {/* Activate Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleActivate}
            disabled={isActivating || !!successMessage}
          >
            {isActivating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ativando...
              </>
            ) : (
              <>
                Ativar Conta
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          {/* Footer */}
          <p className="text-xs text-center text-muted-foreground">
            Ao ativar sua conta, você concorda com os termos de uso e política de privacidade.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

/** Invólucro: fecha a tela e volta pro /login durante a manutenção (o login
 * mostra o aviso) — ver `FechadaNaManutencao`. Os hooks da tela ficam todos
 * dentro de `ActivateAccountConteudo`, nunca depois de um return condicional. */
export default function ActivateAccount() {
  return (
    <FechadaNaManutencao destino="/login">
      <ActivateAccountConteudo />
    </FechadaNaManutencao>
  );
}
